// src/hooks/useMicrosoft.js - Hook for accessing Unified Microsoft Integration data from MicrosoftStore
import { useMemo } from 'react';
import useMicrosoftStore from '../stores/MicrosoftStore';
import { useSocket } from '../contexts/SocketContext';

/**
 * Custom hook for accessing Unified Microsoft integration data and operations
 *
 * Provides access to:
 * - Overall Microsoft account connection status
 * - Outlook Calendar feature status
 * - Microsoft Teams feature status
 *
 * Uses Zustand MicrosoftStore with individual selectors for optimal React re-rendering.
 * Store initialization is handled at app level in SocketContext.
 *
 * @returns {object} Microsoft data, operations, and computed values
 */
export const useMicrosoft = () => {
  const socket = useSocket();

  // 🚨 Use individual selectors instead of destructuring entire store
  // This ensures React re-renders when specific properties change

  // Connection status
  const microsoftConnection = useMicrosoftStore(state => state.microsoftConnection);
  const microsoftConnectionLoading = useMicrosoftStore(state => state.microsoftConnectionLoading);

  // OAuth flow
  const oauthUrl = useMicrosoftStore(state => state.oauthUrl);
  const oauthInitiating = useMicrosoftStore(state => state.oauthInitiating);

  // Disconnection
  const disconnecting = useMicrosoftStore(state => state.disconnecting);

  // Connection state
  const isConnected = useMicrosoftStore(state => state.isConnected);

  // ================================
  // OPERATIONS (functions don't change, so we can get them once)
  // ================================

  const loadMicrosoftConnectionStatus = useMicrosoftStore(state => state.loadMicrosoftConnectionStatus);
  const startMicrosoftOAuth = useMicrosoftStore(state => state.startMicrosoftOAuth);
  const disconnectMicrosoft = useMicrosoftStore(state => state.disconnectMicrosoft);

  // Utility functions
  const isMicrosoftConnected = useMicrosoftStore(state => state.isMicrosoftConnected);
  const getAccountEmail = useMicrosoftStore(state => state.getAccountEmail);
  const isOutlookEnabled = useMicrosoftStore(state => state.isOutlookEnabled);
  const isTeamsEnabled = useMicrosoftStore(state => state.isTeamsEnabled);
  const isTokenExpired = useMicrosoftStore(state => state.isTokenExpired);
  const getGrantedScopes = useMicrosoftStore(state => state.getGrantedScopes);

  // Internal initialization state (for fallback check)
  const _initialized = useMicrosoftStore(state => state._initialized);

  // ================================
  // COMPUTED VALUES (memoized)
  // ================================

  /**
   * Whether user is currently connected to Microsoft
   */
  const connected = useMemo(() => {
    return isMicrosoftConnected();
  }, [isMicrosoftConnected]);

  /**
   * Whether token has expired
   */
  const tokenExpired = useMemo(() => {
    return isTokenExpired();
  }, [isTokenExpired]);

  /**
   * Get connected account email
   */
  const accountEmail = useMemo(() => {
    return getAccountEmail();
  }, [getAccountEmail]);

  /**
   * Check if Outlook Calendar feature is enabled
   */
  const outlookEnabled = useMemo(() => {
    return isOutlookEnabled();
  }, [isOutlookEnabled]);

  /**
   * Check if Microsoft Teams feature is enabled
   */
  const teamsEnabled = useMemo(() => {
    return isTeamsEnabled();
  }, [isTeamsEnabled]);

  /**
   * Get granted scopes
   */
  const grantedScopes = useMemo(() => {
    return getGrantedScopes();
  }, [getGrantedScopes]);

  /**
   * Check if setup requires action (for OAuth flow)
   */
  const requiresSetup = useMemo(() => {
    return !connected || tokenExpired;
  }, [connected, tokenExpired]);

  /**
   * Check if any operation is loading
   */
  const anyLoading = useMemo(() => {
    return microsoftConnectionLoading ||
           oauthInitiating ||
           disconnecting;
  }, [microsoftConnectionLoading, oauthInitiating, disconnecting]);

  /**
   * Get current connection status string
   */
  const currentStatus = useMemo(() => {
    if (!connected) return 'disconnected';
    if (tokenExpired) return 'token_expired';
    return 'connected';
  }, [connected, tokenExpired]);

  /**
   * Get user-friendly status message
   */
  const statusMessage = useMemo(() => {
    const messages = {
      disconnected: 'Not connected to Microsoft',
      connected: 'Connected to Microsoft',
      token_expired: 'Connection expired - please reconnect'
    };
    return messages[currentStatus] || 'Unknown status';
  }, [currentStatus]);

  // ================================
  // FALLBACK INITIALIZATION
  // ================================

  // Fallback initialization check (safety net)
  // If app-level initialization failed but we're authenticated, try fallback
  if (socket.isAuthenticated && !socket.storesInitialized && !_initialized) {
    console.warn('⚠️ useMicrosoft: Store not initialized at app level, trying fallback');
    socket.initializeStore('MicrosoftStore');
  }

  // ================================
  // RETURN INTERFACE
  // ================================

  return {
    // Connection status
    microsoftConnection,
    microsoftConnectionLoading,
    isConnected,
    connected,
    currentStatus,
    statusMessage,
    tokenExpired,
    requiresSetup,

    // OAuth flow
    oauthUrl,
    oauthInitiating,

    // Account details
    accountEmail,
    grantedScopes,

    // Feature statuses
    outlookEnabled,
    teamsEnabled,

    // Disconnection
    disconnecting,

    // Computed values
    anyLoading,

    // Operations
    loadMicrosoftConnectionStatus,
    startMicrosoftOAuth,
    disconnectMicrosoft,

    // Legacy/convenience aliases
    loading: microsoftConnectionLoading
  };
};

export default useMicrosoft;
